/**
 * Validation Rules Editor
 * For editing question validation rules
 */

import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import type { Question } from '../../utils/api/questionBankApi';

interface ValidationRulesEditorProps {
  validation?: Question['validation'];
  questionType: Question['type'];
  onChange: (validation: Question['validation']) => void;
}

export function ValidationRulesEditor({
  validation,
  questionType,
  onChange,
}: ValidationRulesEditorProps) {
  const updateValidation = <K extends keyof NonNullable<Question['validation']>>(
    field: K,
    value: NonNullable<Question['validation']>[K]
  ) => {
    onChange({ ...validation, [field]: value });
  };

  // Show different validation options based on question type
  const showMinMax = questionType === 'number' || questionType === 'text' || questionType === 'textarea';
  const showPattern = questionType === 'text';

  if (!showMinMax && !showPattern) {
    return (
      <div className="text-sm text-muted-foreground p-4 border rounded-lg">
        No validation rules available for {questionType} questions
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Label>Validation Rules</Label>
        <p className="text-sm text-muted-foreground">
          Define constraints for valid answers
        </p>
      </div>

      {showMinMax && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="min">
              {questionType === 'number' ? 'Minimum Value' : 'Minimum Length'}
            </Label>
            <Input
              id="min"
              type="number"
              value={validation?.min ?? ''}
              onChange={(e) => updateValidation('min', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder={questionType === 'number' ? '0' : '5'}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="max">
              {questionType === 'number' ? 'Maximum Value' : 'Maximum Length'}
            </Label>
            <Input
              id="max"
              type="number"
              value={validation?.max ?? ''}
              onChange={(e) => updateValidation('max', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder={questionType === 'number' ? '100' : '500'}
            />
          </div>
        </div>
      )}

      {showPattern && (
        <div className="space-y-2">
          <Label htmlFor="pattern">Pattern (RegEx)</Label>
          <Input
            id="pattern"
            value={validation?.pattern || ''}
            onChange={(e) => updateValidation('pattern', e.target.value || undefined)}
            placeholder="^[A-Z]{2,3}-[0-9]{3}$"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Regular expression pattern for validation (e.g., email, phone number)
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="errorMessage">Custom Error Message</Label>
        <Textarea
          id="errorMessage"
          value={validation?.errorMessage || ''}
          onChange={(e) => updateValidation('errorMessage', e.target.value || undefined)}
          placeholder="Please enter a valid value"
          rows={2}
        />
        <p className="text-xs text-muted-foreground">
          Message shown when validation fails
        </p>
      </div>

      {/* Examples */}
      {showPattern && (
        <div className="bg-muted p-3 rounded-lg text-sm space-y-2">
          <div className="font-medium">Common Patterns:</div>
          <div className="space-y-1 text-xs font-mono">
            <div>Email: <code className="bg-background px-1">^[^\s@]+@[^\s@]+\.[^\s@]+$</code></div>
            <div>Phone: <code className="bg-background px-1">{"^\\+?[1-9]\\d{1,14}$"}</code></div>
            <div>URL: <code className="bg-background px-1">^https?://.*</code></div>
            <div>Alphanumeric: <code className="bg-background px-1">^[a-zA-Z0-9]+$</code></div>
          </div>
        </div>
      )}
    </div>
  );
}
